<template>
  <div>
    <!-- Контролы -->
    <div class="controls" :class="{ 'controls-hidden': !visible }">
      <!-- Кнопка микрофона -->
      <button
        @click="emit('toggle-audio')"
        :class="['control-btn', { active: !audioMuted }]"
        :title="audioMuted ? 'Включить микрофон' : 'Выключить микрофон'"
      >
        <i :class="audioMuted ? 'fas fa-microphone-slash' : 'fas fa-microphone'"></i>
      </button>

      <!-- Кнопка видео (только если есть камера) -->
      <button
        v-if="hasVideo"
        @click="emit('toggle-video')"
        :class="['control-btn', { active: !videoMuted }]"
        :title="videoMuted ? 'Включить камеру' : 'Выключить камеру'"
      >
        <i :class="videoMuted ? 'fas fa-video-slash' : 'fas fa-video'"></i>
      </button>

      <!-- Кнопка переключения камеры (только если доступно) -->
      <button
        v-if="hasVideo && canSwitchCameras"
        @click="emit('switch-camera')"
        class="control-btn camera-switch"
        :title="cameraSwitchTitle"
      >
        <i class="fas fa-sync-alt"></i>
      </button>

      <!-- Кнопка настроек (устройства и качество) -->
      <button
        @click="emit('toggle-device-menu')"
        :class="['control-btn', { active: showDeviceMenu }]"
        title="Настройки устройств и качества"
      >
        <i class="fas fa-cog"></i>
      </button>

      <!-- Кнопка статистики -->
      <button
        @click="emit('toggle-stats')"
        :class="['control-btn', { active: showStats }]"
        title="Показать статистику"
      >
        <i class="fas fa-chart-bar"></i>
      </button>

      <!-- Кнопка копирования ссылки -->
      <button
        @click="emit('copy-link')"
        class="control-btn copy-link"
        title="Скопировать ссылку на комнату"
      >
        <i class="fas fa-link"></i>
      </button>

      <!-- Кнопка выхода -->
      <button
        @click="emit('leave-room')"
        class="control-btn danger"
        title="Покинуть комнату"
      >
        <i class="fas fa-phone-slash"></i>
      </button>
    </div>

    <!-- Индикатор скрытой панели -->
    <div v-if="!visible" class="controls-hint">
      <i class="fas fa-hand-pointer"></i>
      <span>Коснитесь экрана для управления</span>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Компонент панели управления
 * Отображает кнопки управления звонком
 */

import { computed } from 'vue';

// ==================== PROPS ====================

interface Props {
  /** Видимость панели управления */
  visible: boolean;
  /** Флаг выключенного аудио */
  audioMuted: boolean;
  /** Флаг выключенного видео */
  videoMuted: boolean;
  /** Флаг наличия видео */
  hasVideo: boolean;
  /** Можно ли переключать камеры */
  canSwitchCameras: boolean;
  /** Тип текущей камеры */
  cameraType?: 'front' | 'back' | 'unknown';
  /** Показывать ли меню устройств */
  showDeviceMenu: boolean;
  /** Показывать ли статистику */
  showStats: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  cameraType: 'unknown'
});

// ==================== EMITS ====================

const emit = defineEmits<{
  'toggle-audio': [];
  'toggle-video': [];
  'switch-camera': [];
  'toggle-device-menu': [];
  'toggle-stats': [];
  'copy-link': [];
  'leave-room': [];
}>();

// ==================== COMPUTED ====================

/** Текст подсказки для кнопки переключения камеры */
const cameraSwitchTitle = computed(() => {
  if (props.cameraType === 'front') {
    return 'Переключить на заднюю камеру';
  } else if (props.cameraType === 'back') {
    return 'Переключить на фронтальную камеру';
  } else {
    return 'Переключить камеру';
  }
});
</script>

<style scoped>
.controls {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 16px;
  z-index: 100;
  padding: 16px 24px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 32px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 1;
  visibility: visible;
}

.controls.controls-hidden {
  opacity: 0;
  visibility: hidden;
  transform: translateX(-50%) translateY(20px);
  pointer-events: none;
}

.control-btn {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  border: none;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.control-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  opacity: 0;
  transition: opacity 0.2s ease;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
}

.control-btn:hover::before {
  opacity: 1;
}

.control-btn:active {
  transform: translateY(0);
  transition: transform 0.1s ease;
}

.control-btn.active {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  box-shadow: 0 4px 15px rgba(34, 197, 94, 0.3);
}

.control-btn.active:hover {
  background: linear-gradient(135deg, #16a34a, #15803d);
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
}

.control-btn.danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
}

.control-btn.danger:hover {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
}

.control-btn.camera-switch {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

.control-btn.camera-switch:hover {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.control-btn.copy-link {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  box-shadow: 0 4px 15px rgba(139, 92, 246, 0.3);
}

.control-btn.copy-link:hover {
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
}

.controls-hint {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 20px;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 50;
  animation: fadeInOut 3s ease-in-out infinite;
  pointer-events: none;
}

@keyframes fadeInOut {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* Мобильная адаптация */
@media (max-width: 768px) {
  .control-btn {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }

  .controls {
    bottom: 16px;
    padding: 12px 20px;
  }

  .controls.controls-hidden {
    transform: translateX(-50%) translateY(30px);
  }

  .controls-hint {
    bottom: 16px;
    padding: 10px 16px;
    font-size: 0.8rem;
  }
}
</style>
